import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionContext";
import {
  LayoutDashboard,
  CalendarRange,
  Wrench,
  Building2,
  FileText,
  Plane,
  PlaneTakeoff,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Grid2x2,
  Package,
  Database,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const cargoNav = [
  { path: "/cargo/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/cargo/inflight", label: "In Flight", icon: PlaneTakeoff },
  { path: "/cargo/planning", label: "Global Planning", icon: CalendarRange },
  { path: "/cargo/services", label: "Services", icon: Wrench },
  { path: "/cargo/agencies", label: "Agency", icon: Building2 },
  { path: "/cargo/invoicing", label: "Invoicing", icon: FileText },
];

export const CargoLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isMasterAdmin } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = isMasterAdmin
    ? [...cargoNav, { path: "/cargo/backup", label: "Backup", icon: Database }]
    : cargoNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavList = ({ onItemClick }) => (
    <>
      <div className="logo-container">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
            <Package size={22} className="text-[#0f172a]" />
          </div>
          <div className="text-white">
            <p className="font-semibold text-sm leading-tight">Cargo</p>
            <p className="text-xs text-gray-400">BSCA Cargo Management</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <p className="text-xs uppercase text-gray-500 font-semibold px-4 py-2">Main Menu</p>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                onItemClick?.();
              }}
              className={`nav-item w-full text-left ${isActive ? "active" : ""}`}
              data-testid={`cargo-nav-${item.path.split("/").pop()}`}
            >
              <Icon size={20} />
              <span>{item.label}</span>
              {isActive && <ChevronRight size={16} className="ml-auto" />}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t border-gray-700">
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
            <span className="text-white font-bold">{user?.full_name?.charAt(0) || "U"}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-xs text-emerald-400">Cargo Module</p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10"
          data-testid="cargo-logout-btn"
        >
          <LogOut size={18} className="mr-2" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="app-container">
      {/* Fixed Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-[#0f172a] z-[60] flex items-center justify-between gap-2 px-4 md:pl-72 border-b border-[#1e293b]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-400 px-3 py-1 text-xs font-bold uppercase tracking-wide">
            <Package size={14} /> Cargo Module
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open("/dashboard", "_blank", "noopener")}
            className="text-gray-300 hover:text-[#d4af37] hover:bg-[#1e293b] gap-1.5"
            data-testid="switch-to-aviation-btn"
            title="Open Business Aviation in a new tab"
          >
            <Plane size={18} />
            <span className="hidden sm:inline text-xs font-semibold">Business Aviation</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/select-module")}
            className="text-gray-300 hover:text-white hover:bg-[#1e293b] gap-1.5"
            data-testid="modules-btn"
            title="Back to module selection"
          >
            <Grid2x2 size={18} />
            <span className="hidden sm:inline text-xs font-semibold">Modules</span>
          </Button>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex flex-col w-64">
        <NavList />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-14 left-0 right-0 h-16 bg-[#020617] z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
            <Package size={18} className="text-[#0f172a]" />
          </div>
          <span className="text-white font-semibold text-sm">Cargo</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)} className="text-white" data-testid="cargo-mobile-menu-btn">
          <Menu size={24} />
        </Button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="sidebar absolute left-0 top-0 bottom-0 w-64 flex flex-col animate-slide-in">
            <div className="absolute top-4 right-4">
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="text-white">
                <X size={24} />
              </Button>
            </div>
            <NavList onItemClick={() => setMobileMenuOpen(false)} />
          </aside>
        </div>
      )}

      <main className="main-content">{children}</main>
    </div>
  );
};

export default CargoLayout;
